<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import useUserSession from '/@src/composable/useUserSession'

const emit = defineEmits(['close'])
const { t } = useI18n()
const userSession = useUserSession()
</script>

<template>
  <div class="sidebar-panel is-generic">
    <div class="subpanel-header">
      <h3 class="no-mb">{{ t('sidebar-content') }}</h3>
      <div class="panel-close" @click="emit('close')">
        <i aria-hidden="true" class="iconify" data-icon="feather:x"></i>
      </div>
    </div>
    <div class="inner" data-simplebar>
      <ul>
        <!--  新建内容  -->
        <li v-if="userSession.isInternalAdmin" v-has-nested-router-link class="has-children">
          <!--  标题  -->
          <div class="collapse-wrap">
            <a class="parent-link">
              {{ t('sidebar-new-content') }}
              <i
                aria-hidden="true"
                class="iconify"
                data-icon="feather:chevron-right"
              />
            </a>
          </div>
          <!--  新建内容  -->
          <ul>
            <li>
              <RouterLink
                :to="{ name: 'content-article-create' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-book"></i>
                <span>{{ t('create-article') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-video-create' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-video"></i>
                <span>{{ t('create-video') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-link-list', query: { action: 'create' } }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-Website"></i>
                <span>{{ t('create-link') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-roadshow-create' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-stats-up"></i>
                <span>{{ t('create-roadshow') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-report', query: { action: 'create' } }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-sales-report"></i>
                <span>{{ t('create-report') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-ai-content' }"
                class="is-submenu"
              >
                <i class="iconfont icon-AI" aria-hidden="true"></i>
                <span class="ml-3">{{ t('create-ai-content') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-daily-report' }"
                class="is-submenu"
              >
                <i class="iconfont icon-daily-report" aria-hidden="true"></i>
                <span class="ml-3">{{ t('create-custom-report') }}</span>
              </RouterLink>
            </li>
          </ul>
        </li>
        <!--  内容列表  -->
        <li v-if="userSession.isInternalAdmin" v-has-nested-router-link class="has-children">
          <div class="collapse-wrap">
            <a class="parent-link">
              {{ t('sidebar-content-list') }}
              <i
                aria-hidden="true"
                class="iconify"
                data-icon="feather:chevron-right"
              />
            </a>
          </div>
          <ul>
            <li>
              <RouterLink
                :to="{ name: 'content-article-list' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-book"></i>
                <span>{{ t('sidebar-content-list-article') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-video-list' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-video"></i>
                <span>{{ t('sidebar-content-list-videos') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-sources-external-items' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-world"></i>
                <span>{{ t('sidebar-content-external-items') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-roadshow-list' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-stats-up"></i>
                <span>{{ t('sidebar-content-list-roadshow') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink :to="{ name: 'content-report' }" class="is-submenu">
                <i aria-hidden="true" class="lnil lnil-sales-report"></i>
                <span>{{ t('sidebar-content-list-report') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-wallstreet-live' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-Website"></i>
                <span>{{ t('sidebar-content-list-wallstreet-news') }}</span>
              </RouterLink>
            </li>
          </ul>
        </li>
        <!--  数据管理 -->
        <li v-if="userSession.isInternalAdmin" v-has-nested-router-link class="has-children">
          <div class="collapse-wrap">
            <a class="parent-link">
              {{ t('sidebar-content-data-management') }}
              <i
                aria-hidden="true"
                class="iconify"
                data-icon="feather:chevron-right"
              />
            </a>
          </div>
          <ul>
            <li>
              <RouterLink
                :to="{ name: 'content-data-sources' }"
                class="is-submenu"
              >
                <i class="lnil lnil-database" aria-hidden="true"></i>
                <span>{{ t('sidebar-content-data-sources') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-data-charts' }"
                class="is-submenu"
              >
                <i class="lnil lnil-bar-chart" aria-hidden="true"></i>
                <span>{{ t('sidebar-content-data-charts') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-data-groups' }"
                class="is-submenu"
              >
                <i class="lnil lnil-layers" aria-hidden="true"></i>
                <span>{{ t('sidebar-content-data-groups') }}</span>
              </RouterLink>
            </li>
          </ul>
        </li>
        <!-- 订阅源管理 -->
        <li v-if="userSession.isInternalAdmin" v-has-nested-router-link class="has-children">
          <div class="collapse-wrap">
            <a class="parent-link">
              {{ t('sidebar-content-sources-management') }}
              <i
                aria-hidden="true"
                class="iconify"
                data-icon="feather:chevron-right"
              />
            </a>
          </div>
          <ul>
            <li>
              <RouterLink
                :to="{ name: 'content-sources-list' }"
                class="is-submenu"
              >
                <i class="lnil lnil-rss-feed" aria-hidden="true"></i>
                <span>{{ t('sidebar-content-sources-list') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink
                :to="{ name: 'content-sources-groups' }"
                class="is-submenu"
              >
                <i aria-hidden="true" class="lnil lnil-layers"></i>
                <span>{{ t('sidebar-content-sources-group') }}</span>
              </RouterLink>
            </li>
          </ul>
        </li>
        <!-- 金融日历内容 -->
        <li v-if="userSession.isInternalAdmin" v-has-nested-router-link class="has-children calendar-content">
          <div class="collapse-wrap">
            <a class="parent-link">
              {{ t('sidebar-calendar-content') }}
              <i
                aria-hidden="true"
                class="iconify"
                data-icon="feather:chevron-right"
              />
            </a>
          </div>
          <ul style="padding-left: 10px">
            <li>
              <RouterLink :to="{ name: 'content-events' }" class="pl-8">
                <i class="lnil lnil-calendar" aria-hidden="true"></i>
                {{ t('sidebar-content-calendar') }}
              </RouterLink>
            </li>
            <!-- 金融日历配置 -->
            <li>
              <RouterLink :to="{ name: 'content-calendar-list' }">
                <i
                  aria-hidden="true"
                  class="iconify sidebar-svg"
                  data-icon="feather:cpu"
                ></i>
                {{ t('sidebar-content-calendar-config') }}
              </RouterLink>
            </li>
            <!-- 金融日历事件模版配置 -->
            <li>
              <RouterLink :to="{ name: 'content-calendar-event-list' }">
                <i
                  aria-hidden="true"
                  class="iconify sidebar-svg"
                  data-icon="feather:cpu"
                ></i>
                {{ t('sidebar-content-calendar-event-template') }}
              </RouterLink>
            </li>
            <!-- 日历专题列表 -->
            <li>
              <RouterLink :to="{ name: 'content-events-topic-list' }">
                <i
                  class="iconify"
                  data-icon="feather:list"
                  aria-hidden="true"
                ></i>
                {{ t('sidebar-content-calendar-topic-list') }}
              </RouterLink>
            </li>
          </ul>
        </li>
        <!-- ai内容 -->
        <li v-if="userSession.isInternalAdmin" v-has-nested-router-link class="has-children calendar-content">
          <div class="collapse-wrap">
            <a class="parent-link">
              {{ t('sidebar-ai-content') }}
              <i
                aria-hidden="true"
                class="iconify"
                data-icon="feather:chevron-right"
              />
            </a>
          </div>
          <ul style="padding-left: 10px">
            <li>
              <RouterLink :to="{ name: 'content-ai-credit' }">
                <i class="iconfont icon-bot" aria-hidden="true"></i>
                {{ t('sidebar-credit-config') }}
              </RouterLink>
            </li>
          </ul>
        </li>
        <!-- 动态发布 -->
        <li>
          <RouterLink :to="{ name: 'content-posts' }">{{
            t('sidebar-content-posts')
          }}</RouterLink>
        </li>
        <!-- 金融短讯 -->
        <li v-if="userSession.isInternalAdmin">
          <RouterLink :to="{ name: 'content-calendar' }">
            {{ t('sidebar-content-flash') }}
          </RouterLink>
        </li>
        <!-- 市场热点（观点） -->
        <li v-if="userSession.isInternalAdmin">
          <RouterLink :to="{ name: 'content-viewpoint' }">{{
            t('sidebar-content-viewpoint')
          }}</RouterLink>
        </li>
        <!-- 标签管理 -->
        <li v-if="userSession.isInternalAdmin">
          <RouterLink :to="{ name: 'content-tags' }">
            {{ t('sidebar-content-tag') }}
          </RouterLink>
        </li>
        <!-- 企业标签管理 -->
        <li v-if="userSession.isInternalAdmin">
          <RouterLink :to="{ name: 'content-company-tags' }">
            {{ t('sidebar-company-tag') }}
          </RouterLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss">
@import '../../scss/abstracts/_variables.scss';
@import '../../scss/layout/_sidebar-panel.scss';
</style>
